import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { BottomSheetInstance } from '../../../components/bottomSheet/types';

import type { HeaderModal } from './types';

const initialState: {
  betLimitsOpen: boolean;
  // dialogues
  openedModal: HeaderModal | null;
  // all drawers handled by core are also modals on desktop, thus bottom sheets
  openedDrawer: BottomSheetInstance | null;
  openedBottomSheetModal: BottomSheetInstance | null;
  openedBottomSheets: Record<BottomSheetInstance, boolean>;

  isMiniLobbyOpen: boolean;
  isBackToGameHidden: boolean;
  navExpanded: boolean;
  navForcedExpanded: boolean;
  navRoute: string[];
  isBackButtonHidden: boolean;
  settingsTabIndex: number;
} = {
  betLimitsOpen: false,
  openedDrawer: null,
  openedBottomSheets: { Autoplay: false, Statistics: false },
  openedBottomSheetModal: null,
  openedModal: null,
  isMiniLobbyOpen: false,
  isBackToGameHidden: true,
  navExpanded: false,
  navForcedExpanded: false,
  navRoute: [],
  isBackButtonHidden: false,
  settingsTabIndex: 0,
};

const resetNavStateIfEmpty = (state: typeof initialState): void => {
  if (!state.navRoute.length) {
    state.navExpanded = false;
    state.navForcedExpanded = false;
  }
};

export const headerSlice = createSlice({
  name: 'header',
  initialState,
  reducers: {
    setBetLimitsOpen: (state, action: PayloadAction<boolean>) => {
      state.betLimitsOpen = action.payload;
    },
    setOpenedModal: (state, action: PayloadAction<HeaderModal | null>) => {
      state.openedModal = action.payload;
    },
    setOpenedBottomSheetModal: (state, action: PayloadAction<BottomSheetInstance | null>) => {
      state.openedBottomSheetModal = action.payload;
    },
    // intentionally not having a separate setOpenedBottomSheetDrawer because all core drawers are bottom sheets, same cannot be said for modals
    setOpenedDrawer: (state, action: PayloadAction<BottomSheetInstance | null>) => {
      state.openedDrawer = action.payload;
    },
    setBottomSheetOpen: (state, action: PayloadAction<{ instance: BottomSheetInstance; state: boolean }>) => {
      state.openedBottomSheets[action.payload.instance] = action.payload.state;
    },
    setOverrideBottomSheetOpen: (state, action: PayloadAction<{ instance: BottomSheetInstance; state: boolean }>) => {
      Object.keys(state.openedBottomSheets).forEach((key) => {
        if (key !== action.payload.instance) {
          state.openedBottomSheets[key as BottomSheetInstance] = false;
        }
      });
      state.openedBottomSheets[action.payload.instance] = action.payload.state;
    },
    setMiniLobbyOpen: (state, action: PayloadAction<boolean>) => {
      state.isMiniLobbyOpen = action.payload;
      if (!action.payload) {
        state.isBackToGameHidden = true; // reset to hidden state.
      }
    },
    setBackToGameHidden: (state, action: PayloadAction<boolean>) => {
      state.isBackToGameHidden = action.payload;
    },
    setNavExpanded: (state, action: PayloadAction<boolean>) => {
      if (state.navForcedExpanded) return;
      state.navExpanded = action.payload;
    },
    setNavForcedExpanded: (state, action: PayloadAction<boolean>) => {
      state.navForcedExpanded = action.payload;
      if (action.payload) state.navExpanded = true;
    },
    pushNavRoute: (state, action: PayloadAction<string>) => {
      state.navRoute.push(action.payload);
    },
    popNavRoute: (state) => {
      state.navRoute.pop();
      resetNavStateIfEmpty(state);
    },
    replaceNavRoute: (state, action: PayloadAction<string>) => {
      state.navRoute[state.navRoute.length - 1] = action.payload;
    },
    setNavRoute: (state, action: PayloadAction<string[]>) => {
      state.navRoute = action.payload;
      resetNavStateIfEmpty(state);
    },
    setIsBackButtonHidden: (state, action: PayloadAction<boolean>) => {
      state.isBackButtonHidden = action.payload;
    },
    setSettingsTabIndex: (state, action: PayloadAction<number>) => {
      state.settingsTabIndex = action.payload;
    },
  },
});

export const headerActions = headerSlice.actions;
